import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Flame } from "lucide-react";

export default function Login() {
    const { login, formatApiErrorDetail } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const redirectTo = location.state?.from || "/feed";

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            await login(email, password);
            navigate(redirectTo, { replace: true });
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="min-h-screen bg-[var(--crewz-bg)] flex items-center justify-center px-6"
            data-testid="login-page"
        >
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    className="flex items-center gap-2 font-display text-2xl tracking-tighter font-black mb-10"
                >
                    <span className="inline-flex items-center justify-center w-8 h-8 bg-red-600 text-white rounded-sm">
                        <Flame size={18} strokeWidth={2.5} />
                    </span>
                    <span>
                        Crew<span className="text-red-600">Z</span>NatioN
                    </span>
                </Link>

                <p className="overline" data-testid="login-overline">
                    Welcome back
                </p>
                <h1 className="font-display text-4xl font-black tracking-tighter text-white mt-2">
                    Sign in to the Nation.
                </h1>
                <p className="font-body text-sm text-zinc-500 mt-2">
                    Pick up where you left off. Engine warm.
                </p>

                <form onSubmit={onSubmit} className="mt-10 space-y-4" data-testid="login-form">
                    <div>
                        <label className="overline block mb-2 text-zinc-400">Email</label>
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 focus:ring-1 focus:ring-red-600 text-white rounded-sm px-4 py-3 placeholder:text-zinc-600 font-body outline-none"
                            placeholder="you@crewznation.com"
                            data-testid="login-email-input"
                        />
                    </div>
                    <div>
                        <label className="overline block mb-2 text-zinc-400">Password</label>
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-600 focus:ring-1 focus:ring-red-600 text-white rounded-sm px-4 py-3 placeholder:text-zinc-600 font-body outline-none"
                            placeholder="••••••••"
                            data-testid="login-password-input"
                        />
                    </div>

                    {error && (
                        <div
                            className="text-sm text-red-500 font-body bg-red-950/40 border border-red-900/40 px-3 py-2 rounded-sm"
                            data-testid="login-error"
                        >
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white px-4 py-3 rounded-sm font-semibold tracking-wide transition-colors"
                        data-testid="login-submit-btn"
                    >
                        {submitting ? "Signing in…" : "Sign In"}
                    </button>
                </form>

                <p className="text-xs text-zinc-600 font-body mt-6">
                    <Link to="/" className="hover:text-zinc-400">
                        ← Back to home
                    </Link>
                </p>
            </div>
        </div>
    );
}
